import React, { useRef, useMemo, useCallback, useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Save, X } from 'lucide-react';
import {
    escapeHtmlForDescription,
    prepareTaskDescriptionHtmlForSave,
} from '../../../utils/task_description_html';
import { compressImageForUpload } from '../../../utils/compress_image';
import { upload_task_description_image } from '../../../utils/talent_services';
import './TaskDescriptionEditor.css';

const DESCRIPTION_COLORS = ['#1f2937', '#dc2626', '#1e3a5f', '#14532d', '#6366f1'];

const DESCRIPTION_FORMATS = [
    'header',
    'bold',
    'italic',
    'underline',
    'list',
    'link',
    'color',
    'image',
];

function insertChecklistHtml(quill, onHtmlChange) {
    if (!quill) return;
    const range = quill.getSelection(true);
    const index = range ? range.index : Math.max(0, quill.getLength() - 1);
    let lines = [];
    if (range && range.length > 0) {
        const text = quill.getText(range.index, range.length);
        lines = text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
        quill.deleteText(range.index, range.length, 'user');
    }
    const html =
        lines.length > 0
            ? `<ul class="task-desc-checklist">${lines
                  .map((l) => `<li><input type="checkbox"> ${escapeHtmlForDescription(l)}</li>`)
                  .join('')}</ul>`
            : '<ul class="task-desc-checklist"><li><input type="checkbox"> </li></ul>';
    quill.clipboard.dangerouslyPasteHTML(index, html, 'user');
    quill.setSelection(index + 1, 0, 'silent');
    onHtmlChange?.(quill.root.innerHTML);
}

function handleChecklistEnter(e, editorEl, onHtmlChange) {
    if (e.key !== 'Enter' || e.shiftKey) return false;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return false;
    let node = sel.anchorNode;
    if (!node || !editorEl.contains(node)) return false;

    let li = null;
    while (node && node !== editorEl) {
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'LI') {
            const ul = node.closest('ul.task-desc-checklist');
            if (ul) {
                li = node;
                break;
            }
        }
        node = node.parentNode;
    }
    if (!li) return false;

    e.preventDefault();
    e.stopPropagation();

    const doc = editorEl.ownerDocument;
    const range = sel.getRangeAt(0);
    const newLi = doc.createElement('li');
    newLi.innerHTML = '<input type="checkbox"> ';
    const spaceNode = Array.from(newLi.childNodes).find((n) => n.nodeType === Node.TEXT_NODE);

    try {
        const endRange = doc.createRange();
        endRange.setStart(range.endContainer, range.endOffset);
        endRange.setEnd(li, li.childNodes.length);
        const fragment = endRange.extractContents();
        if (fragment.firstChild) newLi.appendChild(fragment);
        if (li.nextSibling) li.parentNode.insertBefore(newLi, li.nextSibling);
        else li.parentNode.appendChild(newLi);
        const target = spaceNode || newLi;
        const offset = spaceNode ? 1 : 0;
        const newRange = doc.createRange();
        newRange.setStart(target, offset);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
    } catch (_) {
        li.parentNode.insertBefore(newLi, li.nextSibling);
    }
    onHtmlChange?.(editorEl.innerHTML);
    return true;
}

function imageUploadErrorMessage(err) {
    const code = typeof err === 'string' ? err : err?.message;
    if (code === 'invalid_type') return 'Solo se permiten imágenes (JPEG, PNG, WebP o GIF).';
    if (code === 'too_large') return 'La imagen es demasiado pesada. Probá con una más liviana.';
    if (code === 'denied' || code === 'not_found') return 'No tenés permiso para subir imágenes en esta tarea.';
    return 'No se pudo subir la imagen. Intentá de nuevo.';
}

/**
 * Editor enriquecido (ReactQuill) para descripción de tarea.
 */
const TaskDescriptionEditor = ({
    value,
    onChange,
    onSave,
    onCancel,
    saving = false,
    placeholder = 'Escribe la descripción… También puedes pegar o insertar imágenes.',
    editorKey = 'default',
    taskId = null,
    token = null,
    uploadImageFn = null,
}) => {
    const quillRef = useRef(null);
    const editorWrapperRef = useRef(null);
    const initialHtmlRef = useRef(value || '');
    const uploadImageRef = useRef(null);
    const onChangeRef = useRef(onChange);
    const uploadingLockRef = useRef(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    initialHtmlRef.current = value || '';
    onChangeRef.current = onChange;

    const getEditorHtml = useCallback(() => {
        const quill = quillRef.current?.getEditor?.();
        return quill?.root?.innerHTML ?? '';
    }, []);

    const insertChecklist = useCallback(() => {
        const quill = quillRef.current?.getEditor?.();
        insertChecklistHtml(quill, (html) => onChangeRef.current?.(html));
    }, []);

    const uploadAndInsertImage = useCallback(
        async (file) => {
            const canUpload = uploadImageFn
                ? Boolean(token)
                : Boolean(file && taskId && token);
            if (!file || !canUpload) {
                window.alert('No se puede subir la imagen en este momento.');
                return;
            }
            if (uploadingLockRef.current) return;

            uploadingLockRef.current = true;
            setUploadingImage(true);
            try {
                const compressed = await compressImageForUpload(file);
                const result = uploadImageFn
                    ? await uploadImageFn(compressed)
                    : await upload_task_description_image(token, taskId, compressed);
                if (!result || result === 'INVALID' || result.ok !== true || !result.url) {
                    throw new Error(result?.message || 'upload_failed');
                }

                const quill = quillRef.current?.getEditor?.();
                if (!quill) return;
                // Quill bloquea inserts con source 'user' si está deshabilitado (readOnly).
                quill.enable(true);
                const range = quill.getSelection(true);
                const index = range ? range.index : Math.max(0, quill.getLength() - 1);
                quill.insertEmbed(index, 'image', result.url, 'user');
                quill.insertText(index + 1, '\n', 'user');
                quill.setSelection(index + 2, 0, 'silent');
                onChangeRef.current?.(quill.root.innerHTML);
            } catch (err) {
                window.alert(imageUploadErrorMessage(err));
            } finally {
                uploadingLockRef.current = false;
                setUploadingImage(false);
            }
        },
        [taskId, token, uploadImageFn]
    );

    uploadImageRef.current = uploadAndInsertImage;

    const imageHandler = useCallback(() => {
        const canUpload = uploadImageFn ? Boolean(token) : Boolean(taskId && token);
        if (!canUpload) {
            window.alert('No se puede subir la imagen en este momento.');
            return;
        }
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/jpeg,image/png,image/webp,image/gif');
        input.click();
        input.onchange = () => {
            const file = input.files?.[0];
            if (file) uploadImageRef.current?.(file);
        };
    }, [taskId, token, uploadImageFn]);

    const handleSaveClick = useCallback(() => {
        onSave?.(getEditorHtml());
    }, [onSave, getEditorHtml]);

    const modules = useMemo(
        () => ({
            toolbar: {
                container: [
                    [{ header: [2, 3, false] }],
                    ['bold', 'italic', 'underline'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['link', 'image'],
                    [{ color: DESCRIPTION_COLORS }],
                    ['checklist'],
                    ['clean'],
                ],
                handlers: {
                    checklist: insertChecklist,
                    image: imageHandler,
                },
            },
            clipboard: {
                matchVisual: false,
            },
        }),
        [insertChecklist, imageHandler]
    );

    useEffect(() => {
        const quill = quillRef.current?.getEditor?.();
        if (!quill) return;
        // Solo sincronizar al montar / cambiar de tarea — no tras cada onChange
        // (eso borraba la imagen recién insertada con el draft viejo).
        const html = initialHtmlRef.current;
        if (html && quill.root.innerHTML !== html) {
            quill.root.innerHTML = html;
        }

        const collectImageFiles = (dataTransfer) => {
            if (!dataTransfer) return [];
            const fromFiles = [...(dataTransfer.files || [])].filter((f) =>
                (f.type || '').startsWith('image/')
            );
            if (fromFiles.length > 0) return fromFiles;

            const items = dataTransfer.items ? [...dataTransfer.items] : [];
            const fromItems = [];
            for (const item of items) {
                if (item.kind === 'file' && (item.type || '').startsWith('image/')) {
                    const file = item.getAsFile();
                    if (file) fromItems.push(file);
                }
            }
            return fromItems;
        };

        const onPaste = (e) => {
            const imageFiles = collectImageFiles(e.clipboardData);
            if (imageFiles.length > 0) {
                e.preventDefault();
                e.stopPropagation();
                (async () => {
                    for (const file of imageFiles) {
                        await uploadImageRef.current?.(file);
                    }
                })();
                return;
            }

            window.setTimeout(() => {
                const cleaned = prepareTaskDescriptionHtmlForSave(quill.root.innerHTML);
                if (cleaned !== quill.root.innerHTML) {
                    quill.root.innerHTML = cleaned;
                }
                onChangeRef.current?.(quill.root.innerHTML);
            }, 0);
        };

        const onDrop = (e) => {
            const imageFiles = collectImageFiles(e.dataTransfer);
            if (imageFiles.length === 0) return;
            e.preventDefault();
            e.stopPropagation();
            (async () => {
                for (const file of imageFiles) {
                    await uploadImageRef.current?.(file);
                }
            })();
        };

        const onDragOver = (e) => {
            if (collectImageFiles(e.dataTransfer).length > 0) {
                e.preventDefault();
            }
        };

        quill.root.addEventListener('paste', onPaste, true);
        quill.root.addEventListener('drop', onDrop, true);
        quill.root.addEventListener('dragover', onDragOver, true);
        return () => {
            quill.root.removeEventListener('paste', onPaste, true);
            quill.root.removeEventListener('drop', onDrop, true);
            quill.root.removeEventListener('dragover', onDragOver, true);
        };
    }, [editorKey]);

    // ReactQuill pasa un editor "unprivileged" (getHTML/getText…), sin `.root`.
    const handleQuillChange = useCallback((content, _delta, _source, editor) => {
        const html =
            typeof content === 'string'
                ? content
                : typeof editor?.getHTML === 'function'
                  ? editor.getHTML()
                  : '';
        onChangeRef.current?.(html);
    }, []);

    useEffect(() => {
        const wrapper = editorWrapperRef.current;
        if (!wrapper) return;
        const editorEl = wrapper.querySelector('.ql-editor');
        if (!editorEl) return;

        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onCancel?.();
                return;
            }
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                onSave?.(editorEl.innerHTML);
                return;
            }
            handleChecklistEnter(e, editorEl, () => {
                onChange(editorEl.innerHTML);
            });
        };

        editorEl.addEventListener('keydown', onKeyDown);
        return () => editorEl.removeEventListener('keydown', onKeyDown);
    }, [onSave, onCancel, onChange]);

    const busy = saving || uploadingImage;

    return (
        <div
            className={`task-description-edit-panel${uploadingImage ? ' is-uploading-image' : ''}`}
            ref={editorWrapperRef}
        >
            <div className="statistics-description-quill">
                <ReactQuill
                    key={editorKey}
                    ref={quillRef}
                    theme="snow"
                    defaultValue={value || ''}
                    onChange={handleQuillChange}
                    modules={modules}
                    formats={DESCRIPTION_FORMATS}
                    placeholder={placeholder}
                />
            </div>
            {uploadingImage && (
                <div className="task-description-upload-status" aria-live="polite">
                    Subiendo imagen…
                </div>
            )}
            <div className="task-description-edit-actions">
                <button
                    type="button"
                    className="btn-edit btn-edit-primary"
                    onClick={handleSaveClick}
                    disabled={busy}
                    aria-label="Guardar descripción"
                >
                    <Save size={14} />
                    {saving ? 'Guardando…' : 'Guardar'}
                </button>
                <button
                    type="button"
                    className="btn-edit btn-edit-secondary"
                    onClick={onCancel}
                    disabled={busy}
                    aria-label="Cancelar edición"
                >
                    <X size={14} />
                    Cancelar
                </button>
            </div>
        </div>
    );
};

export default TaskDescriptionEditor;
